import {
  buildPrioritizedVersionedTransaction,
  categorize,
  getTransactionFees,
  type ISolanaSigner,
  makeSolanaConnection,
  PREFERRED_COMMITMENT_LEVEL,
  type SolanaSignData,
  type SolanaSignMessageOptions,
  traceSolanaTransaction,
} from '@polaris/core/solana'
import { debug } from '@polaris/core/utils'
import { base64, utf8 } from '@scure/base'
import { isVersionedTransaction } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import { useCallback, useMemo } from 'react'
import { wrapCatchCategorize } from '@/src/utils/error'

export const useSolanaWalletSigner = () => {
  const {
    publicKey,
    sendTransaction,
    signMessage: signSolanaMessage,
    wallet,
  } = useWallet()

  const deriveAddress = useCallback(() => {
    return publicKey?.toBase58()
  }, [publicKey])

  const estimateFees = useCallback(
    async ({
      transaction,
      connection = makeSolanaConnection(),
    }: SolanaSignData) =>
      getTransactionFees(connection, transaction).then(
        ({ totalFee }) => totalFee,
      ),
    [],
  )

  const signAndBroadcast = useCallback(
    async ({
      transaction,
      connection = makeSolanaConnection(),
      sendOptions,
    }: SolanaSignData) => {
      let tx = transaction
      let opts = sendOptions

      const {
        context: { slot: minContextSlot },
        value,
      } = await connection.getLatestBlockhashAndContext(
        PREFERRED_COMMITMENT_LEVEL,
      )

      let lastValidBlockHeight = value.lastValidBlockHeight

      if (isVersionedTransaction(tx)) {
        const { transaction, lastValidBlockHeight: lvbh } =
          await buildPrioritizedVersionedTransaction(
            connection,
            tx,
            tx.message.staticAccountKeys[0],
          )
        tx = transaction
        lastValidBlockHeight = lvbh
      } else {
        tx.lastValidBlockHeight = lastValidBlockHeight
      }

      // Default send options
      if (!opts) {
        opts = {
          preflightCommitment: PREFERRED_COMMITMENT_LEVEL,
          skipPreflight: true,
          // Must be 0 to use Helius staked connection
          // https://www.helius.dev/blog/how-to-land-transactions-on-solana#implement-a-robust-retry-logic
          maxRetries: 0,
          minContextSlot,
        }
      }

      // Log wallet information
      const walletName = wallet?.adapter?.name
      debug(`solana signAndBroadcast with wallet ${walletName}`)

      // Send the transaction
      const signature = await sendTransaction(tx, connection, opts)

      await traceSolanaTransaction({
        txHash: signature,
        connection,
        lastValidBlockHeight,
      })

      return signature
    },
    [sendTransaction, wallet?.adapter?.name],
  )

  const signMessage = useCallback(
    async ({ message }: SolanaSignMessageOptions) => {
      const result = await signSolanaMessage!(utf8.decode(message))
      return base64.encode(result)
    },
    [signSolanaMessage],
  )

  return useMemo(
    () => ({
      deriveAddress: wrapCatchCategorize(deriveAddress, categorize),
      estimateFees: wrapCatchCategorize(estimateFees, categorize),
      signAndBroadcast: wrapCatchCategorize(signAndBroadcast, categorize),
      signMessage: wrapCatchCategorize(signMessage, categorize),
    }),
    [deriveAddress, estimateFees, signAndBroadcast, signMessage],
  ) satisfies ISolanaSigner
}
